import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { useNotification } from '../hooks/useNotification';
import Notification from './Notification';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { getFirstAllowedPage, loading: permissionsLoading, userRole } = usePermissions();
  const { notifications, removeNotification } = useNotification();

  // Redirecionar automaticamente quando as permissões estiverem carregadas após login
  useEffect(() => {
    if (loginSuccess && isAuthenticated && !permissionsLoading && userRole) {
      const firstPage = getFirstAllowedPage();
      if (firstPage) {
        navigate(firstPage, { replace: true });
        setLoginSuccess(false);
      }
    }
  }, [loginSuccess, isAuthenticated, permissionsLoading, userRole, getFirstAllowedPage, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setLoginSuccess(false);

    try {
      await login(email, password);
      // Marcar login como bem-sucedido - o useEffect vai redirecionar quando as permissões estiverem prontas
      setLoginSuccess(true);
    } catch (error) {
      // Sempre garantir que o loading seja resetado
      setLoading(false);
      setLoginSuccess(false);

      // Extrair mensagem de erro apropriada
      let errorMessage = 'Erro ao fazer login. Tente novamente.';

      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Mensagem específica para Unauthorized
      if (errorMessage.toLowerCase().includes('unauthorized') ||
          errorMessage.toLowerCase().includes('credenciais') ||
          errorMessage.toLowerCase().includes('inválidas')) {
        errorMessage = 'Credenciais inválidas. Verifique seu email e senha.';
      }

      setError(errorMessage);
    }
  };


  return (
      <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/logo.png" alt="SendingTo Express" className="logo-image" />
          </div>
          <h1>SendingTo Express</h1>
          <p>Sistema de Gestão</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="seu@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <HiEyeOff size={20} /> : <HiEye size={20} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          <div className="forgot-password-link">
            <button
              type="button"
              onClick={() => navigate('/password-recovery')}
              className="link-button"
            >
              Esqueci minha senha
            </button>
          </div>
        </form>
      </div>

      <div className="notifications-container">
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            duration={notification.duration}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default Login;